import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Calendar from "./Calendar";
import MobileAgendaView from "./components/MobileAgendaView";
import MobileDayGrid from "./components/MobileDayGrid";
import MobileMonthView from "./components/MobileMonthView";
import { useEvents } from "./hooks/useEvents";
import { useHolidayEvents } from "./hooks/useHolidayEvents";
import "./App.css";

// TEMP: expose Firestore audit function
import { auditFirestoreEvents } from "./utils/firestoreAudit";
import {
  authFetch,
  clearAuthToken,
  fetchMe,
  fetchIcalLink,
  getAuthExpiredEventName,
  getAuthTokenClaims,
  getAuthTokenExpiryMs,
  getAuthToken,
  login,
  refreshSession,
  rotateIcalLink,
} from "./api/authApi";

const BACKEND_HEARTBEAT_INTERVAL_MS = 15000;
const showLegacyIcalMenuItem = false;
const MOBILE_VIEW_STORAGE_KEY = "mobileView";
const MOBILE_DAY_DATE_STORAGE_KEY = "mobileDayDate";
const MOBILE_AGENDA_DATE_STORAGE_KEY = "mobileAgendaDate";

function readStoredDay(key, fallbackDate = new Date()) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return normalizeToDay(fallbackDate);
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return normalizeToDay(fallbackDate);
    return normalizeToDay(parsed);
  } catch {
    return normalizeToDay(fallbackDate);
  }
}

function readStoredMobileView() {
  try {
    const raw = String(localStorage.getItem(MOBILE_VIEW_STORAGE_KEY) || "").trim().toLowerCase();
    return raw === "day" || raw === "month" || raw === "agenda" ? raw : "day";
  } catch {
    return "day";
  }
}

export function detectMobileLayout() {
  const qs = new URLSearchParams(window.location.search);
  const forced = qs.get("mobile");
  if (forced === "1" || forced === "true") return true;
  if (forced === "0" || forced === "false") return false;

  const ua = navigator.userAgent || "";
  const byUa = /iPhone|iPad|iPod|Android/i.test(ua);
  const byWidth = window.matchMedia("(max-width: 900px)").matches;
  const byPointer = window.matchMedia("(pointer: coarse) and (hover: none) and (max-width: 1100px)").matches;

  // Handles iOS Safari "Request Desktop Website" where UA may look desktop.
  const touchPoints = Number(navigator.maxTouchPoints || 0);
  const hoverCapable = window.matchMedia("(hover: hover)").matches;
  const viewportWidth = Math.min(
    Number(window.innerWidth || 0) || Number.MAX_SAFE_INTEGER,
    Number(document.documentElement?.clientWidth || 0) || Number.MAX_SAFE_INTEGER
  );
  const byTouchScreen = touchPoints > 0 && !hoverCapable && viewportWidth > 0 && viewportWidth <= 1024;

  return byUa || byWidth || byPointer || byTouchScreen;
}

function formatTodayHeaderDate(date) {
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatMonthHeader(date) {
  return date.toLocaleDateString("en-US", {
    month: "long",
  });
}

function formatAgendaHeader(date) {
  const start = normalizeToDay(new Date(date));
  const startDow = start.getDay();
  const mondayOffset = startDow === 0 ? -6 : 1 - startDow;
  start.setDate(start.getDate() + mondayOffset);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
}

function formatHealthTimestamp(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "";
  }
}

function normalizeToDay(date) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function toSafeDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return normalizeToDay(new Date());
  return date;
}

function isSameRange(prevRange, nextRange) {
  const prevStart = toSafeDate(prevRange?.startDate).getTime();
  const prevEnd = toSafeDate(prevRange?.endDate).getTime();
  const nextStart = toSafeDate(nextRange?.startDate).getTime();
  const nextEnd = toSafeDate(nextRange?.endDate).getTime();
  return prevStart === nextStart && prevEnd === nextEnd;
}

function eventMatchesSearch(event, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return true;
  const attachmentsText = Array.isArray(event.extendedProps?.attachments)
    ? event.extendedProps.attachments
        .map((item) => `${String(item?.label || "")} ${String(item?.file_name || "")} ${String(item?.url || "")}`)
        .join(" ")
    : "";
  const fields = [
    event.title,
    event.start,
    event.end,
    event.extendedProps?.location,
    event.extendedProps?.project_name,
    event.extendedProps?.invoice_notes,
    event.extendedProps?.internal_notes,
    event.extendedProps?.category_name,
    attachmentsText,
  ]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");
  return fields.includes(q);
}

function getUserInitials(value) {
  const text = String(value || "").trim();
  if (!text) return "?";
  const lowered = text.toLowerCase();
  if (lowered.includes("colin")) return "CK";
  if (lowered.includes("eira")) return "EK";
  const parts = text
    .replace(/[_.]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authDiagnostics, setAuthDiagnostics] = useState("");
  const [icalPanelOpen, setIcalPanelOpen] = useState(false);
  const [icalLoading, setIcalLoading] = useState(false);
  const [icalError, setIcalError] = useState("");
  const [icalLink, setIcalLink] = useState("");
  const [icalCopied, setIcalCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchTerm, setMobileSearchTerm] = useState("");
  const [showUkHolidays, setShowUkHolidays] = useState(
    () => localStorage.getItem("showUkHolidays") !== "false"
  );
  const [showSingaporeHolidays, setShowSingaporeHolidays] = useState(
    () => localStorage.getItem("showSingaporeHolidays") === "true"
  );
  const [timeFormat, setTimeFormat] = useState(
    () => (localStorage.getItem("timeFormat") === "24h" ? "24h" : "12h")
  );
  const [desktopHolidayRange, setDesktopHolidayRange] = useState(() => ({
    startDate: normalizeToDay(new Date()),
    endDate: normalizeToDay(new Date()),
  }));
  const [mobileHolidayRange, setMobileHolidayRange] = useState(() => ({
    startDate: normalizeToDay(new Date()),
    endDate: normalizeToDay(new Date()),
  }));

  const [backendHealth, setBackendHealth] = useState({
    isReachable: true,
    lastCheckedAt: 0,
    lastFailureAt: 0,
    lastError: "",
  });
  const previousReachableRef = useRef(true);

  // ── Mobile detection ──────────────────────────────────────
  const [isMobile, setIsMobile] = useState(
    () => detectMobileLayout()
  );
  const [mobileView, setMobileView] = useState(() => readStoredMobileView()); // "day" | "month" | "agenda"
  const [mobileDayDate, setMobileDayDate] = useState(() => readStoredDay(MOBILE_DAY_DATE_STORAGE_KEY));
  const [mobileAgendaDate, setMobileAgendaDate] = useState(() => readStoredDay(MOBILE_AGENDA_DATE_STORAGE_KEY));
  const [mobileCreateRequest, setMobileCreateRequest] = useState(null);
  const [mobileEditRequest, setMobileEditRequest] = useState(null);
  const [desktopView, setDesktopView] = useState("calendar"); // "calendar" | "agenda"
  const [desktopCalendarState, setDesktopCalendarState] = useState(() => ({
    view: "dayGridMonth",
    date: new Date(),
  }));
  const [desktopLastCalendarState, setDesktopLastCalendarState] = useState(() => ({
    view: "dayGridMonth",
    date: new Date(),
  }));
  const [desktopAgendaDate, setDesktopAgendaDate] = useState(() => normalizeToDay(new Date()));
  const [mobileTodayToken, setMobileTodayToken] = useState(0);
  const currentUserLabel = String(currentUser || username || "").trim();
  const currentUserInitials = getUserInitials(currentUserLabel);
  const currentUserAvatarLabel = currentUserLabel || "Not signed in";

  // ── Auth-bar height — drives mobile-shell top offset ────────
  const authBarRef = useRef(null);
  const [authBarHeight, setAuthBarHeight] = useState(56);

  // Events for mobile month view — only load after auth to avoid pre-login 401 noise
  const { events: mobileEvents, reload: reloadMobileEvents } = useEvents({ enabled: isAuthenticated });
  const mobileFilteredEvents = useMemo(
    () => mobileEvents.filter((event) => eventMatchesSearch(event, mobileSearchTerm)),
    [mobileEvents, mobileSearchTerm]
  );
  const activeHolidayRange = isMobile ? mobileHolidayRange : desktopHolidayRange;
  const { holidayEvents, holidayError } = useHolidayEvents({
    startDate: activeHolidayRange.startDate,
    endDate: activeHolidayRange.endDate,
    showUkHolidays,
    showSingaporeHolidays,
  });
  const mobileMergedEvents = useMemo(
    () => [...mobileFilteredEvents, ...holidayEvents],
    [mobileFilteredEvents, holidayEvents]
  );
  const agendaEvents = useMemo(() => {
    return [...mobileMergedEvents].sort((left, right) => {
      const leftStart = left?.start ? new Date(left.start).getTime() : Number.MAX_SAFE_INTEGER;
      const rightStart = right?.start ? new Date(right.start).getTime() : Number.MAX_SAFE_INTEGER;
      return leftStart - rightStart;
    });
  }, [mobileMergedEvents]);
  const isAgendaActive = isMobile ? mobileView === "agenda" : desktopView === "agenda";

  useEffect(() => {
    const handler = () => setIsMobile(detectMobileLayout());
    const mqWidth = window.matchMedia("(max-width: 900px)");
    const mqPointer = window.matchMedia("(pointer: coarse) and (max-width: 1100px)");

    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    mqWidth.addEventListener("change", handler);
    mqPointer.addEventListener("change", handler);

    handler();

    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
      mqWidth.removeEventListener("change", handler);
      mqPointer.removeEventListener("change", handler);
    };
  }, []);

  useEffect(() => {
    if (isAgendaActive) {
      setToolsOpen(false);
    }
  }, [isAgendaActive]);

  // Lock body scroll on mobile so iOS Safari doesn't bounce the whole page.
  // Inner scroll containers use overflow-y:scroll / -webkit-overflow-scrolling:touch.
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isMobile]);

  useEffect(() => {
    const el = authBarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setAuthBarHeight(el.getBoundingClientRect().bottom);
    });
    ro.observe(el);
    setAuthBarHeight(el.getBoundingClientRect().bottom);
    return () => ro.disconnect();
  }, []);

  // TEMP: attach audit function to window for console use
  useEffect(() => {
    window.auditFirestore = auditFirestoreEvents;
    console.log("🔥 Firestore audit available as window.auditFirestore(userId)");
  }, []);

  useEffect(() => {
    let cancelled = false;

    const checkBackend = async () => {
      try {
        const response = await authFetch("/ping", {
          method: "GET",
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`Backend ping failed (${response.status})`);
        }

        if (cancelled) return;
        setBackendHealth((current) => ({
          ...current,
          isReachable: true,
          lastCheckedAt: Date.now(),
          lastFailureAt: 0,
          lastError: "",
        }));
      } catch (err) {
        if (cancelled) return;
        const now = Date.now();
        setBackendHealth((current) => ({
          ...current,
          isReachable: false,
          lastCheckedAt: now,
          lastFailureAt: current.lastFailureAt || now,
          lastError: String(err?.message || "Network error"),
        }));
      }
    };

    checkBackend();
    const intervalId = window.setInterval(checkBackend, BACKEND_HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const wasReachable = previousReachableRef.current;
    if (wasReachable && !backendHealth.isReachable) {
      const failureAt = formatHealthTimestamp(backendHealth.lastFailureAt) || "unknown time";
      setAuthError((current) => current || `Backend offline detected at ${failureAt}.`);
    }
    previousReachableRef.current = backendHealth.isReachable;
  }, [backendHealth.isReachable, backendHealth.lastFailureAt]);

  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        setAuthLoading(false);
        return;
      }

      try {
        const me = await fetchMe();
        setIsAuthenticated(true);
        setCurrentUser(me?.username || me?.user || "");
        setAuthDiagnostics("");
      } catch (err) {
        const status = Number(err?.status || 0);
        if (status === 401) {
          clearAuthToken();
          setIsAuthenticated(false);
          setCurrentUser("");
          setAuthDiagnostics("Auth check failed: 401 (session expired or invalid token)");
        } else {
          // Preserve existing token for transient backend errors.
          const claims = getAuthTokenClaims();
          setAuthError((current) => current || "Backend temporarily unavailable. Session preserved.");
          setAuthDiagnostics(
            `Auth check failed: ${status || "network"} (transient backend/connectivity issue)`
          );
          setIsAuthenticated(true);
          setCurrentUser(claims?.sub || claims?.username || "");
        }
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    const eventName = getAuthExpiredEventName();
    const handleAuthExpired = () => {
      setIsAuthenticated(false);
      setCurrentUser("");
      setPassword("");
      setAuthError("Session expired. Please sign in again.");
      setAuthDiagnostics("Auth check failed: 401 (session expired or invalid token)");
    };

    window.addEventListener(eventName, handleAuthExpired);
    return () => window.removeEventListener(eventName, handleAuthExpired);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    let timerId = null;

    const scheduleRefresh = () => {
      if (cancelled) return;

      const expiryMs = getAuthTokenExpiryMs();
      const now = Date.now();
      const fallbackDelayMs = 15 * 60 * 1000;
      const refreshLeadMs = 5 * 60 * 1000;
      let delayMs = fallbackDelayMs;

      if (expiryMs > now) {
        delayMs = Math.max(30 * 1000, expiryMs - now - refreshLeadMs);
      }

      timerId = window.setTimeout(async () => {
        try {
          const refreshed = await refreshSession();
          const usernameFromRefresh = refreshed?.user?.username || "";
          if (usernameFromRefresh) {
            setCurrentUser(usernameFromRefresh);
          }
        } catch {
          // Global 401 handling will transition to sign-in if refresh fails.
        } finally {
          scheduleRefresh();
        }
      }, delayMs);
    };

    scheduleRefresh();

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthDiagnostics("");

    try {
      await login(username.trim(), password.trim());
      const me = await fetchMe();
      setIsAuthenticated(true);
      setCurrentUser(me?.username || me?.user || username || "");
      setPassword("");
      setAuthDiagnostics("");
    } catch (err) {
      const status = Number(err?.status || 0);
      if (status === 401) {
        setAuthDiagnostics("Login failed: 401 (invalid credentials or expired session)");
      } else if (status > 0) {
        setAuthDiagnostics(`Login failed: ${status} (backend/connectivity issue)`);
      } else {
        setAuthDiagnostics("Login failed: network/connectivity issue");
      }
      setAuthError(err.message || "Login failed");
    }
  };

  const handleLogout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
    setCurrentUser("");
    setPassword("");
    setIcalPanelOpen(false);
    setIcalError("");
    setIcalLink("");
    setIcalCopied(false);
  };

  useEffect(() => {
    if (!toolsOpen && !mobileSearchOpen) return;
    const onPointerDownAway = (event) => {
      const root = authBarRef.current;
      if (root && event?.target instanceof Node && root.contains(event.target)) {
        return;
      }
      setToolsOpen(false);
      setMobileSearchOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDownAway);
    return () => window.removeEventListener("pointerdown", onPointerDownAway);
  }, [toolsOpen, mobileSearchOpen]);

  const loadIcalLink = async () => {
    setIcalLoading(true);
    setIcalError("");
    setIcalCopied(false);
    try {
      const data = await fetchIcalLink();
      setIcalLink(String(data?.webcal_url || data?.feed_url || ""));
    } catch (err) {
      setIcalError(err.message || "Unable to load iCal link");
    } finally {
      setIcalLoading(false);
    }
  };

  const handleToggleIcalPanel = async () => {
    const nextOpen = !icalPanelOpen;
    setIcalPanelOpen(nextOpen);
    if (nextOpen && !icalLink && !icalLoading) {
      await loadIcalLink();
    }
  };

  const handleRotateIcalLink = async () => {
    setIcalLoading(true);
    setIcalError("");
    setIcalCopied(false);
    try {
      const data = await rotateIcalLink();
      setIcalLink(String(data?.webcal_url || data?.feed_url || ""));
    } catch (err) {
      setIcalError(err.message || "Unable to rotate iCal link");
    } finally {
      setIcalLoading(false);
    }
  };

  const handleCopyIcalLink = async () => {
    if (!icalLink) return;
    try {
      await navigator.clipboard.writeText(icalLink);
      setIcalCopied(true);
    } catch {
      setIcalError("Copy failed. Please copy manually.");
    }
  };

  const handleMobileToday = () => {
    const now = normalizeToDay(new Date());
    if (mobileView === "agenda") {
      setMobileAgendaDate(now);
      return;
    }
    setMobileDayDate(now);
    setMobileTodayToken((value) => value + 1);
  };

  const openMobileAgenda = useCallback(() => {
    const anchor = mobileView === "day" ? mobileDayDate : mobileAgendaDate;
    setMobileAgendaDate(normalizeToDay(anchor || new Date()));
    setMobileView("agenda");
    reloadMobileEvents().catch(() => {
      // Keep navigation responsive even if refresh fails transiently.
    });
  }, [mobileAgendaDate, mobileDayDate, mobileView, reloadMobileEvents]);

  useEffect(() => {
    if (!isAuthenticated || mobileView !== "agenda") return;
    reloadMobileEvents().catch(() => {
      // Non-blocking refresh for agenda re-entry.
    });
  }, [isAuthenticated, mobileView, reloadMobileEvents]);

  const handleMobileDayDateChange = useCallback((date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    const normalized = normalizeToDay(date);
    setMobileDayDate((prev) => {
      const prevNormalized = normalizeToDay(prev);
      return prevNormalized.getTime() === normalized.getTime() ? prev : normalized;
    });
  }, []);

  const handleToggleUkHolidays = useCallback(() => {
    setShowUkHolidays((current) => !current);
  }, []);

  const handleToggleSingaporeHolidays = useCallback(() => {
    setShowSingaporeHolidays((current) => !current);
  }, []);

  useEffect(() => {
    localStorage.setItem("showUkHolidays", String(showUkHolidays));
  }, [showUkHolidays]);

  useEffect(() => {
    localStorage.setItem("showSingaporeHolidays", String(showSingaporeHolidays));
  }, [showSingaporeHolidays]);

  useEffect(() => {
    localStorage.setItem("timeFormat", timeFormat);
  }, [timeFormat]);

  useEffect(() => {
    localStorage.setItem(MOBILE_VIEW_STORAGE_KEY, mobileView);
  }, [mobileView]);

  useEffect(() => {
    localStorage.setItem(MOBILE_DAY_DATE_STORAGE_KEY, normalizeToDay(mobileDayDate).toISOString());
  }, [mobileDayDate]);

  useEffect(() => {
    localStorage.setItem(MOBILE_AGENDA_DATE_STORAGE_KEY, normalizeToDay(mobileAgendaDate).toISOString());
  }, [mobileAgendaDate]);

  useEffect(() => {
    if (mobileView !== "day") return;
    setMobileHolidayRange({
      startDate: normalizeToDay(mobileDayDate),
      endDate: normalizeToDay(mobileDayDate),
    });
  }, [mobileDayDate, mobileView]);

  const handleMobileVisibleRangeChange = useCallback((startDate, endDate) => {
    const normalizedRange = {
      startDate: toSafeDate(startDate),
      endDate: toSafeDate(endDate),
    };

    setMobileHolidayRange((prevRange) => {
      if (isSameRange(prevRange, normalizedRange)) return prevRange;
      return normalizedRange;
    });
  }, []);

  const handleDesktopVisibleRangeChange = useCallback((startDate, endDate) => {
    const normalizedRange = {
      startDate: toSafeDate(startDate),
      endDate: toSafeDate(endDate),
    };

    setDesktopHolidayRange((prevRange) => {
      if (isSameRange(prevRange, normalizedRange)) return prevRange;
      return normalizedRange;
    });
  }, []);

  const returnToDesktopCalendar = useCallback(() => {
    setDesktopCalendarState((current) => {
      const nextView = desktopLastCalendarState?.view || current.view || "dayGridMonth";
      const fallbackDate = current.date instanceof Date && !Number.isNaN(current.date.getTime())
        ? current.date
        : new Date();
      const nextDate =
        desktopLastCalendarState?.date instanceof Date && !Number.isNaN(desktopLastCalendarState.date.getTime())
          ? desktopLastCalendarState.date
          : fallbackDate;
      return {
        view: nextView,
        date: nextDate,
      };
    });
    setDesktopView("calendar");
  }, [desktopLastCalendarState]);

  const backendOfflineMessage = backendHealth.isReachable
    ? ""
    : `Backend offline since ${formatHealthTimestamp(backendHealth.lastFailureAt) || "unknown"}. ${backendHealth.lastError || "Connection failed."}`;
  const isTwentyFourHour = String(timeFormat || "").trim().toLowerCase().startsWith("24");

  if (authLoading) {
    return <div className="auth-loading">Checking session...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-screen">
        <form className="auth-form" onSubmit={handleLogin}>
          <h1>Sign In</h1>
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            required
          />

          <label htmlFor="password">Password</label>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              required
              style={{ flex: 1, paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: "absolute", right: 8, background: "none",
                border: "none", cursor: "pointer", padding: 0,
                fontSize: "0.8rem", color: "#6b7280", height: "auto",
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          {authError && <p className="auth-error">{authError}</p>}
          {authDiagnostics ? <p className="auth-debug-line">{authDiagnostics}</p> : null}
          {!backendHealth.isReachable ? (
            <p className="backend-health-error">{backendOfflineMessage}</p>
          ) : null}

          <button type="submit">Sign In</button>
        </form>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {!backendHealth.isReachable ? (
        <div className="app-backend-warning" role="status" aria-live="polite">
          {backendOfflineMessage}
        </div>
      ) : null}
      <div className="auth-bar" ref={authBarRef}>
        <button
          type="button"
          className={`user-avatar-trigger ${isAuthenticated ? "is-signed-in" : "is-signed-out"}`.trim()}
          onClick={(e) => {
            e.stopPropagation();
            if (isAgendaActive) return;
            setMobileSearchOpen(false);
            setToolsOpen((v) => !v);
          }}
          aria-haspopup="menu"
          aria-expanded={toolsOpen}
          aria-label={isAuthenticated ? `Signed in as ${currentUserAvatarLabel}` : "Not signed in"}
          title={isAuthenticated ? `Signed in as ${currentUserAvatarLabel}` : "Not signed in"}
        >
          <span className="user-avatar-circle" aria-hidden="true">
            {isAuthenticated ? (
              <span className="user-avatar-initials">{currentUserInitials}</span>
            ) : (
              <span className="user-avatar-person" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                  <path d="M12 12.5a4.75 4.75 0 1 0-4.75-4.75A4.76 4.76 0 0 0 12 12.5Zm0 2.25c-4.15 0-7.5 2.66-7.5 5.95 0 .69.56 1.25 1.25 1.25h12.5c.69 0 1.25-.56 1.25-1.25 0-3.29-3.35-5.95-7.5-5.95Z" />
                </svg>
              </span>
            )}
          </span>
        </button>
        {isMobile ? (
          <div className="mobile-left-controls">
            {mobileView === "day" ? (
              <button
                type="button"
                className="mobile-month-back"
                onClick={() => setMobileView("month")}
                aria-label="Back to month view"
              >
                <span className="mobile-month-back-icon" aria-hidden="true">&lt;</span>
                <span className="mobile-month-back-text">{formatMonthHeader(mobileDayDate)}</span>
              </button>
            ) : mobileView === "agenda" ? (
              <span className="mobile-header-date">{formatAgendaHeader(mobileAgendaDate)}</span>
            ) : (
              <span className="mobile-header-date">{formatTodayHeaderDate(new Date())}</span>
            )}
          </div>
        ) : null}
        {icalPanelOpen && (
          <div className="ical-panel" role="region" aria-label="iCal subscription link panel">
            <div className="ical-panel-title">Apple Calendar Subscription</div>
            <input
              className="ical-panel-input"
              type="text"
              readOnly
              value={icalLink}
              placeholder={icalLoading ? "Loading..." : "No link loaded"}
            />
            <div className="ical-panel-actions">
              <button type="button" onClick={loadIcalLink} disabled={icalLoading}>Refresh Link</button>
              <button type="button" onClick={handleCopyIcalLink} disabled={!icalLink || icalLoading}>Copy</button>
              <button type="button" onClick={handleRotateIcalLink} disabled={icalLoading}>Rotate</button>
            </div>
            {icalCopied && <div className="ical-panel-status">Copied.</div>}
            {icalError && <div className="ical-panel-error">{icalError}</div>}
          </div>
        )}
        <div className="mobile-right-controls">
          {isMobile && (
            <button
              type="button"
              className="mobile-agenda-trigger"
              onClick={(e) => {
                e.stopPropagation();
                if (mobileView === "agenda") {
                  setMobileView("month");
                  return;
                }
                openMobileAgenda();
              }}
              aria-label={mobileView === "agenda" ? "Back to month view" : "Open agenda view"}
            >
              {mobileView === "agenda" ? "Month" : "Agenda"}
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              className="search-trigger"
              onClick={(e) => {
                e.stopPropagation();
                setToolsOpen(false);
                setMobileSearchOpen((v) => !v);
              }}
              aria-label="Search events"
              aria-expanded={mobileSearchOpen}
            />
          )}
          {!isAgendaActive ? null : null}
        </div>
        {!isAgendaActive && toolsOpen && (
          <div
            className="tools-menu"
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            {isMobile ? (
              <>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={showUkHolidays}
                  className={`tools-menu-check ${showUkHolidays ? "is-checked" : ""}`}
                  onClick={handleToggleUkHolidays}
                >
                  <span className="tools-menu-checkmark" aria-hidden="true">{showUkHolidays ? "✓" : ""}</span>
                  <span>UK Holidays</span>
                </button>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={showSingaporeHolidays}
                  className={`tools-menu-check ${showSingaporeHolidays ? "is-checked" : ""}`}
                  onClick={handleToggleSingaporeHolidays}
                >
                  <span className="tools-menu-checkmark" aria-hidden="true">{showSingaporeHolidays ? "✓" : ""}</span>
                  <span>Singapore Holidays</span>
                </button>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={isTwentyFourHour}
                  className={`tools-menu-check ${isTwentyFourHour ? "is-checked" : ""}`}
                  onClick={() => setTimeFormat((prev) => (String(prev || "").trim().toLowerCase().startsWith("24") ? "12h" : "24h"))}
                >
                  <span className="tools-menu-checkmark" aria-hidden="true">{isTwentyFourHour ? "✓" : ""}</span>
                  <span>24-hour time</span>
                </button>
              </>
            ) : null}
            {showLegacyIcalMenuItem ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setToolsOpen(false);
                  handleToggleIcalPanel();
                }}
              >
                iPhone Feed
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setToolsOpen(false);
                handleLogout();
              }}
            >
              Sign Out
            </button>
          </div>
        )}
        {isMobile && mobileSearchOpen && (
          <div
            className="mobile-search-panel"
            style={{ top: Math.max(0, Math.ceil(authBarHeight) + 6) }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-search-wrap">
              <input
                type="search"
                className="mobile-search-input"
                value={mobileSearchTerm}
                onChange={(e) => setMobileSearchTerm(e.target.value)}
                placeholder="Search events..."
                autoFocus
              />
              {mobileSearchTerm && (
                <button
                  type="button"
                  className="mobile-search-clear"
                  onClick={() => setMobileSearchTerm("")}
                  aria-label="Clear search"
                >
                  x
                </button>
              )}
            </div>
          </div>
        )}

      </div>
      {isMobile ? (
          <div className="mobile-shell" style={{ top: Math.max(0, Math.ceil(authBarHeight) - 1) }}>
            {holidayError ? (
              <div className="mobile-holiday-warning" role="status" aria-live="polite">
              {holidayError}
            </div>
          ) : null}
          {mobileView === "day" && (
            <MobileDayGrid 
              selectedDate={mobileDayDate}
              searchTerm={mobileSearchTerm}
              onDateChange={handleMobileDayDateChange}
              extraEvents={holidayEvents}
              createRequest={mobileCreateRequest}
              editRequest={mobileEditRequest}
              timeFormat={timeFormat}
            />
          )}
          {mobileView === "month" && (
            <MobileMonthView
              events={mobileMergedEvents}
              today={new Date()}
              anchorDate={mobileDayDate}
              todayToken={mobileTodayToken}
              onVisibleRangeChange={handleMobileVisibleRangeChange}
              onDayPress={(date) => {
                setMobileDayDate(normalizeToDay(date));
                setMobileView("day");
              }}
            />
          )}
          {mobileView === "agenda" && (
            <MobileAgendaView
              events={mobileMergedEvents}
              selectedDate={mobileAgendaDate}
              today={new Date()}
              timeFormat={timeFormat}
              onWeekAnchorChange={(date) => {
                if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
                setMobileAgendaDate(normalizeToDay(date));
              }}
              onCreateEvent={(date) => {
                const baseDate = normalizeToDay(date || mobileAgendaDate || new Date());
                setMobileDayDate(baseDate);
                setMobileEditRequest(null);
                setMobileCreateRequest({ token: Date.now(), date: baseDate });
                setMobileView("day");
              }}
              onEditEvent={(event) => {
                const start = event?.start ? new Date(event.start) : new Date();
                const baseDate = Number.isNaN(start.getTime()) ? normalizeToDay(new Date()) : normalizeToDay(start);
                setMobileDayDate(baseDate);
                setMobileCreateRequest(null);
                setMobileEditRequest({ token: Date.now(), event });
                setMobileView("day");
              }}
              onDayPress={(date) => {
                setMobileDayDate(normalizeToDay(date));
                setMobileCreateRequest(null);
                setMobileEditRequest(null);
                setMobileView("day");
              }}
            />
          )}
          <button
            type="button"
            className="mobile-today-fab"
            onClick={handleMobileToday}
          >
            Today
          </button>

        </div>
      ) : (
        <>
          {desktopView === "agenda" ? (
            <div className="desktop-agenda-shell">
              <MobileAgendaView
                events={agendaEvents}
                selectedDate={desktopAgendaDate}
                today={new Date()}
                timeFormat={timeFormat}
                onBackToCalendar={returnToDesktopCalendar}
                isDesktop={true}
                onWeekAnchorChange={(date) => {
                  setDesktopAgendaDate((current) => {
                    const fallbackDate =
                      current instanceof Date && !Number.isNaN(current.getTime())
                        ? current
                        : new Date();
                    const nextDate =
                      date instanceof Date && !Number.isNaN(date.getTime())
                        ? normalizeToDay(date)
                        : fallbackDate;
                    const currentTime = fallbackDate.getTime();
                    const nextTime = nextDate.getTime();
                    if (currentTime === nextTime) {
                      return current;
                    }
                    return nextDate;
                  });
                }}
                onDayPress={(date) => {
                  setDesktopCalendarState({
                    view: "timeGridDay",
                    date: normalizeToDay(date),
                  });
                  setDesktopView("calendar");
                }}
              />
            </div>
          ) : (
            <Calendar
              holidayEvents={holidayEvents}
              holidayError={holidayError}
              showUkHolidays={showUkHolidays}
              showSingaporeHolidays={showSingaporeHolidays}
              timeFormat={timeFormat}
              onTimeFormatChange={setTimeFormat}
              onToggleUkHolidays={handleToggleUkHolidays}
              onToggleSingaporeHolidays={handleToggleSingaporeHolidays}
              onVisibleRangeChange={handleDesktopVisibleRangeChange}
              desktopAgendaOpen={desktopView === "agenda"}
              onToggleDesktopAgenda={() => {
                if (desktopView === "agenda") {
                  returnToDesktopCalendar();
                  return;
                }
                setDesktopLastCalendarState((current) => ({
                  view: desktopCalendarState.view || current.view || "dayGridMonth",
                  date:
                    desktopCalendarState.date instanceof Date && !Number.isNaN(desktopCalendarState.date.getTime())
                      ? desktopCalendarState.date
                      : current.date,
                }));
                setDesktopAgendaDate(() => {
                  if (desktopCalendarState.view === "dayGridMonth") {
                    return normalizeToDay(new Date());
                  }
                  if (desktopCalendarState.date instanceof Date && !Number.isNaN(desktopCalendarState.date.getTime())) {
                    return normalizeToDay(desktopCalendarState.date);
                  }
                  return normalizeToDay(new Date());
                });
                setDesktopView("agenda");
              }}
              initialCalendarView={desktopCalendarState.view}
              initialCalendarDate={desktopCalendarState.date}
              onCalendarStateChange={({ view, date }) => {
                setDesktopCalendarState((current) => {
                  const nextView = view || current.view || "dayGridMonth";
                  const nextDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : current.date;
                  const currentTime = current.date instanceof Date ? current.date.getTime() : NaN;
                  const nextTime = nextDate instanceof Date ? nextDate.getTime() : NaN;
                  if (current.view === nextView && currentTime === nextTime) {
                    return current;
                  }
                  return { view: nextView, date: nextDate };
                });
                if (desktopView !== "agenda") {
                  setDesktopLastCalendarState((current) => {
                    const nextView = view || current.view || "dayGridMonth";
                    const currentDate =
                      current.date instanceof Date && !Number.isNaN(current.date.getTime())
                        ? current.date
                        : new Date();
                    const nextDate =
                      date instanceof Date && !Number.isNaN(date.getTime())
                        ? date
                        : currentDate;
                    const currentTime = currentDate.getTime();
                    const nextTime = nextDate.getTime();
                    if (current.view === nextView && currentTime === nextTime) {
                      return current;
                    }
                    return { view: nextView, date: nextDate };
                  });
                }
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;


  